<template>
  <div>
    <Message severity="info" class="mt-0" :closable="false">
      {{ t("automatedScenarios.declencheur.choose_bulk_audience.label") }}
    </Message>

    <PanelAccordion :key="panelsKeys" :panels="panels">
      <template #choice_list>
        <div class="field-wrapper">
          <div class="audience-mode-cards mb-3">
            <label
              for="audience-mode-all"
              class="audience-mode-card"
              :class="{ 'audience-mode-card--active': audienceMode === 'all_contacts' }"
            >
              <RadioButton
                v-model="audienceMode"
                input-id="audience-mode-all"
                value="all_contacts"
                class="audience-mode-card__radio-sr"
              />
              <span class="audience-mode-card__icon">
                <i class="far fa-users" />
              </span>
              <span class="audience-mode-card__body">
                <span class="audience-mode-card__title">
                  {{
                    t(
                      "automatedScenarios.declencheur.choose_bulk_audience.all_contacts_option"
                    )
                  }}
                </span>
                <span class="audience-mode-card__hint">
                  {{
                    t(
                      "automatedScenarios.declencheur.choose_bulk_audience.all_contacts_row_hint"
                    )
                  }}
                </span>
              </span>
              <span
                v-if="audienceMode === 'all_contacts'"
                class="audience-mode-card__check"
                aria-hidden="true"
              >
                <i class="fas fa-check" />
              </span>
            </label>

            <label
              for="audience-mode-lists"
              class="audience-mode-card"
              :class="{ 'audience-mode-card--active': audienceMode === 'lists' }"
            >
              <RadioButton
                v-model="audienceMode"
                input-id="audience-mode-lists"
                value="lists"
                class="audience-mode-card__radio-sr"
              />
              <span class="audience-mode-card__icon">
                <i class="far fa-list-check" />
              </span>
              <span class="audience-mode-card__body">
                <span class="audience-mode-card__title">
                  {{
                    t(
                      "automatedScenarios.declencheur.choose_bulk_audience.specific_lists_option"
                    )
                  }}
                </span>
                <span class="audience-mode-card__hint">
                  {{
                    t(
                      "automatedScenarios.declencheur.choose_bulk_audience.specific_lists_hint"
                    )
                  }}
                </span>
              </span>
              <span
                v-if="audienceMode === 'lists'"
                class="audience-mode-card__check"
                aria-hidden="true"
              >
                <i class="fas fa-check" />
              </span>
            </label>
          </div>

          <div v-if="audienceMode === 'lists'">
            <label class="block form-label mb-2">
              {{
                t(
                  "automatedScenarios.declencheur.choose_bulk_audience.send_campaign_to"
                )
              }}
            </label>
            <div class="lists-row">
              <div class="lists-input">
                <MultiSelect
                  v-model="lists"
                  data-key="value"
                  filter
                  :options="listsOptions"
                  option-label="label"
                  option-group-label="label"
                  option-group-children="items"
                  :placeholder="
                    t(
                      'automatedScenarios.declencheur.choose_bulk_audience.select_lists_placeholder'
                    )
                  "
                  class="w-full form-multiselect"
                >
                  <template #value="slotProps">
                    <span v-if="!slotProps.value || slotProps.value.length === 0">
                      {{ slotProps.placeholder }}
                    </span>
                    <span v-else>
                      {{
                        slotProps.value.length === 1
                          ? t(
                            "automatedScenarios.declencheur.choose_bulk_audience.selected_lists_count_singular",
                            { n: slotProps.value.length },
                          )
                          : t(
                            "automatedScenarios.declencheur.choose_bulk_audience.selected_lists_count_plural",
                            { n: slotProps.value.length },
                          )
                      }}
                    </span>
                  </template>
                  <template #optiongroup="slotProps">
                    <div class="flex align-items-center">
                      <i
                        :class="optionGroupIconClass(slotProps.option.code)"
                      />
                      <div>{{ slotProps.option.label }}</div>
                    </div>
                  </template>
                </MultiSelect>
                <FieldErrors
                  :key="componentFieldErrorsKey"
                  :errors="formValidationErrors"
                  field="lists"
                  class="mt-2"
                />
              </div>
              <div class="lists-btn">
                <ListCreatePanel />
              </div>
            </div>

            <div v-if="lists.length" class="field-wrapper mt-3">
              <label class="block form-label mb-2">
                {{
                  t(
                    "automatedScenarios.declencheur.choose_bulk_audience.selected_lists"
                  )
                }}
              </label>
              <div class="flex flex-wrap gap-2">
                <div v-for="list in lists" :key="list.value">
                  <Chip :label="list.label" removable @remove="removeList(list)" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #setup_segmentation>
        <div v-if="mountedComponent" class="field-group pt-2">
          <div class="field-group-content">
            <div class="field-wrapper">
              <ButtonGroup
                v-model="segmentTheListData"
                :options="segmentTheListOptions"
                :title="
                  t(
                    'automatedScenarios.declencheur.choose_bulk_audience.segment_audience_question'
                  )
                "
                custom-class-title="block mb-2 form-label"
              />
              <FieldErrors
                :key="componentFieldErrorsKey"
                :errors="formValidationErrors"
                field="segmentation_config"
              />
            </div>
          </div>
        </div>
        <SegmentationsPanel
          v-if="showSegmentationsPanel"
          class="mt-3"
          v-model:model-value="segmentsConfiguration"
          origin="campaign_bulk_audience_setup"
          :show-prompt-a-i="true"
          size="small"
        />
      </template>
    </PanelAccordion>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent, onBeforeMount, PropType, ref, SetupContext, toRaw, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { List } from '@/composables/GraphQL';
import { OperatorType, ShopsLists } from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import MultiSelect from 'primevue/multiselect';
import RadioButton from 'primevue/radiobutton';
import Message from 'primevue/message';
import Chip from 'primevue/chip';
import { PanelAccordion } from '@/components/flows/operators/shared/layout';
import type { SpmPanelAccordionInterface } from '@/components/flows/operators/shared/layout/PanelAccordion.vue';
import { ButtonGroup } from '@/components/flows/operators/shared';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import ListCreatePanel from '@/components/mes-lists/ListCreatePanel.vue';
import { myListsState } from '@/composables/mes-listes/ListsManage';
import { asInt } from '@/helpers';
import type { AudienceSetupData } from './index';
import { createDefaultData } from './index';

interface ListOption {
  label: string;
  value: number;
}

interface ListGroup {
  label: string;
  code: string;
  items: ListOption[];
}

function normalizeAllContactsFlag(v: unknown): boolean {
  return v === '1' || v === 1 || v === true;
}

export default defineComponent({
  name: 'TriggerAudienceSetupForm',
  components: {
    ListCreatePanel,
    ButtonGroup,
    FieldErrors,
    SegmentationsPanel,
    MultiSelect,
    RadioButton,
    PanelAccordion,
    Message,
    Chip,
  },

  props: {
    modelValue: {
      type: Object as PropType<AudienceSetupData>,
      required: true,
      default() {
        return createDefaultData();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: AudienceSetupData; formValidation: any }, context: SetupContext) {
    const { t } = useI18n();
    const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);
    const rawSegmentTheList = props.modelValue.segment_the_list as string | number | undefined;
    const segmentTheListData = ref(
      rawSegmentTheList === '1' || Number(rawSegmentTheList) === 1 ? '1' : String(rawSegmentTheList ?? '0'),
    );
    const mountedComponent = ref(false);
    const panelsKeys = ref(0);

    const panels: SpmPanelAccordionInterface[] = [
      {
        id: 'choice_list',
        title: t('automatedScenarios.declencheur.choose_bulk_audience.panel_lists_title'),
      },
      {
        id: 'setup_segmentation',
        title: t('automatedScenarios.declencheur.choose_bulk_audience.panel_segmentation_title'),
      },
    ];

    const formValidationErrors = ref(props.formValidation);
    const componentFieldErrorsKey = ref(0);

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    const segmentTheListOptions: Array<{ value: string; label: string }> = [
      { value: '0', label: t('no') },
      { value: '1', label: t('yes') },
    ];

    const listsOptions = ref<ListGroup[]>([
      {
        label: t('automatedScenarios.declencheur.choose_bulk_audience.lists_group'),
        code: 'lists',
        items: [],
      },
      {
        label: t('automatedScenarios.declencheur.choose_bulk_audience.segments_group'),
        code: 'segments',
        items: [],
      },
    ]);

    const lists = ref<ListOption[]>([]);
    // Initialize audienceMode from modelValue synchronously so the radio cards
    // never flash the default state before onBeforeMount's async work completes.
    const audienceMode = ref<'all_contacts' | 'lists'>(
      normalizeAllContactsFlag(props.modelValue.all_contacts) ? 'all_contacts' : 'lists',
    );
    const segmentsConfiguration = ref<any>(null);

    const isAllContactsMode = computed(() => audienceMode.value === 'all_contacts');

    const showSegmentationsPanel = computed(
      () => segmentTheListData.value === '1' && segmentsConfiguration.value !== null,
    );

    const optionGroupIconClass = (code: string) => {
      if (code === 'lists') return 'far fa-list mr-2';
      return 'far fa-filter mr-2';
    };

    const removeList = (listToRemove: ListOption) => {
      lists.value = lists.value.filter((l) => l.value !== listToRemove.value);
    };

    const update = () => {
      if (mountedComponent.value) {
        const allContacts = isAllContactsMode.value;
        context.emit('update:modelValue', createDefaultData({
          lists: allContacts ? [] : lists.value.map((item) => item.value),
          all_contacts: allContacts ? '1' : '0',
          segment_the_list: segmentTheListData.value,
          segmentation_config: segmentsConfiguration.value,
        }));
      }
    };

    const fetchListsByType = async (type: string): Promise<ListOption[]> => {
      const { items, err } = await List<ShopsLists>({
        name: 'ShopsLists',
        settings: {
          filter: [
            { field: 'id_shop', operator: OperatorType.Equals, value: idShop.value },
            { field: 'active', operator: OperatorType.Equals, value: 1 },
            { field: 'type', operator: OperatorType.Equals, value: type },
            { field: 'valid', operator: OperatorType.Equals, value: 1 },
          ],
          order: [{ field: 'id_shop_list', type: 'ASC' }],
          limit: 0,
          offset: 0,
        },
        fields: ['id_shop_list', 'name'],
      });

      if (err === '') {
        return items.map((item) => ({
          label: item.name,
          value: item.id_shop_list,
        }));
      }

      return [];
    };

    const initListsOptions = async () => {
      listsOptions.value[0].items = await fetchListsByType('list');
      listsOptions.value[1].items = await fetchListsByType('segment');
    };

    // Switching to "all contacts" mode clears any previously selected lists,
    // but keeps the segmentation choice intact (segmentation is now optional
    // in both modes — the user is free to keep or change their answer).
    watch(audienceMode, (mode) => {
      if (!mountedComponent.value) return;
      if (mode === 'all_contacts' && lists.value.length > 0) {
        lists.value = [];
      }
    });

    // When switching to "Yes", ensure config exists or SegmentationsPanel never mounts (empty block).
    watch(segmentTheListData, (v) => {
      if (!mountedComponent.value) return;
      if (v === '1' && segmentsConfiguration.value == null) {
        segmentsConfiguration.value = [];
      }
    });

    watch(
      [lists, segmentTheListData, segmentsConfiguration, audienceMode],
      update,
      { deep: true },
    );

    watch(() => myListsState.refreshComponent, async () => {
      if (myListsState.refreshComponent !== 0) {
        const newListId = myListsState.refreshComponent;
        await initListsOptions();
        myListsState.refreshComponent = 0;

        if (isAllContactsMode.value) {
          return;
        }

        let matchingLists = listsOptions.value[0].items.filter(
          (item) => item.value === asInt(newListId),
        );
        if (!matchingLists.length) {
          matchingLists = listsOptions.value[1].items.filter(
            (item) => item.value === asInt(newListId),
          );
        }

        if (matchingLists.length) {
          const { label, value } = matchingLists[0];
          lists.value.push({ label, value });
        }
        update();
      }
    });

    onBeforeMount(async () => {
      await initListsOptions();

      if (props.modelValue.segmentation_config) {
        segmentsConfiguration.value = toRaw(props.modelValue.segmentation_config);
      } else if (
        props.modelValue.segment_the_list === '1'
        || Number(props.modelValue.segment_the_list) === 1
      ) {
        segmentsConfiguration.value = [];
      }

      // audienceMode is already initialized from props.modelValue at ref
      // creation time (see above), no need to re-assign here.
      if (audienceMode.value === 'lists') {
        props.modelValue.lists.forEach((listId) => {
          let matchingLists = listsOptions.value[0].items.filter((item) => item.value === listId);
          if (!matchingLists.length) {
            matchingLists = listsOptions.value[1].items.filter((item) => item.value === listId);
          }
          if (matchingLists.length) {
            const { label, value } = matchingLists[0];
            lists.value.push({ label, value });
          }
        });
      }

      mountedComponent.value = true;
      update();
    });

    return {
      t,
      panelsKeys,
      panels,
      lists,
      listsOptions,
      audienceMode,
      segmentsConfiguration,
      componentFieldErrorsKey,
      formValidationErrors,
      segmentTheListData,
      segmentTheListOptions,
      mountedComponent,
      removeList,
      isAllContactsMode,
      showSegmentationsPanel,
      optionGroupIconClass,
    };
  },
});
</script>

<style lang="scss" scoped>
.audience-mode-cards {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.audience-mode-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  padding-right: 2.25rem; // room for the check badge
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(.audience-mode-card--active) {
    border-color: #cbd5e1;
    background: #f9fafb;
  }

  &--active {
    border-color: #22c55e;
    background: #f0fdf4;
    box-shadow: 0 0 0 1px #22c55e inset;

    .audience-mode-card__icon {
      background: #dcfce7;
      color: #15803d;
    }

    .audience-mode-card__title {
      color: #14532d;
    }
  }

  // Visually hide the native radio but keep it accessible via keyboard / screen readers.
  &__radio-sr {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &__icon {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 1.05rem;
    transition:
      background 0.15s ease,
      color 0.15s ease;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #111827;
    line-height: 1.25;
  }

  &__hint {
    font-size: 0.8125rem;
    line-height: 1.4;
    color: #6b7280;
  }

  &__check {
    position: absolute;
    top: 0.625rem;
    right: 0.625rem;
    width: 1.25rem;
    height: 1.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #22c55e;
    color: #fff;
    font-size: 0.625rem;
  }
}

.field-wrapper {
  .p-multiselect {
    width: 100%;
  }

  .lists-row {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .lists-input {
    flex: 1;
    min-width: 0;
  }

  .lists-btn {
    flex-shrink: 0;
    align-self: flex-start;

    :deep(.p-button) {
      min-height: 2.5rem;
      white-space: nowrap;
    }
  }

  .form-multiselect {
    :deep(.p-multiselect-label) {
      padding: 0.5rem 0.75rem;
    }

    border: 1px solid #babfc3 !important;
    border-radius: 4px !important;

    &:hover {
      border-color: #babfc3 !important;
    }

    &.p-focus {
      border-color: #babfc3 !important;
      box-shadow: none !important;
    }
  }

  .form-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }
}
</style>
